import {
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { BusinessRole } from "@1games-ru/region-shared/dist/enums/business/role";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { Case2Icon, Exit2Icon, Plus2Icon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import { setInfo } from "@/domains/business/slice";
import { useEmployeesHandlers } from "@/domains/business/hooks/useEmployeesHandlers";
import BusinessSellContainer from "@/domains/business/containers/tablet/BusinessSellContainer";
import BusinessUpBalanceContainer from "@/domains/business/containers/tablet/BusinessUpBalanceContainer";
import BusinessImprovementsPreviousContainer from "@/domains/business/containers/tablet/tabs/improvements/BusinessImprovementsPreviousContainer";
import BusinessDismissModal from "@/domains/business/components/BusinessDismissModal";
import BusinessMainInfo from "@/domains/business/components/BusinessMainInfo";
import BusinessView from "@/domains/business/components/BusinessView";

type Props = {
  info: MyBusiness | GetBusinessInfoResponse;
  isFirstRender?: boolean;
  onClose: () => void;
  device?: DeviceEnum;
};

export default function BusinessInfoContainer({ info, isFirstRender, onClose, device }: Props) {
  const { t } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isSellShow, setIsSellShow] = useState(false);
  const [isDismissShow, setIsDismissShow] = useState(false);
  const [isUpBalanceShow, setIsUpBalanceShow] = useState(false);

  const { isSelfRemovingLoading, removeEmployeeSelfHandler } = useEmployeesHandlers();

  const isOwner = info?.role === BusinessRole.Owner;
  const address = info ? `${info?.address?.streetName} ${info?.address?.index}` : "";

  const showModal = useCallback(() => {
    if (isOwner) {
      setIsSellShow(true);
    } else {
      setIsDismissShow(true);
    }
  }, [isOwner]);

  const onDismissConfirm = useCallback(() => {
    if (info) {
      if (device === DeviceEnum.TABLET) {
        removeEmployeeSelfHandler(info?.id, onClose);
      } else {
        removeEmployeeSelfHandler(info?.id);
      }
      dispatch(setInfo(null));
    }
  }, [device, dispatch, info, onClose, removeEmployeeSelfHandler]);

  const onUpBalanceModal = useCallback(() => {
    setIsUpBalanceShow(true);
  }, []);

  const closeUpBalanceModal = useCallback(() => {
    setIsUpBalanceShow(false);
  }, []);

  useEffect(() => {
    return () => {
      setIsSellShow(false);
      setIsDismissShow(false);
      setIsUpBalanceShow(false);
    };
  }, []);

  const header = (
    <div className="flex items-center gap-2.5 w-full">
      <div className="text-18 text-white font-bold">
        {info?.name} №{info?.id}
      </div>
      <div className=" bg-white flex items-center gap-1 py-[0.3125rem] pl-[0.3125rem] pr-[0.4375rem] rounded-[0.3125rem] uppercase">
        <div className="size-2 mx-0.5 bg-dark-900 rounded-[0.125rem] rotate-45" />
        <div className="text-12 text-[#0F0F0F] font-bold leading-0">
          {t(`business.level.${info?.level}`)}
        </div>
      </div>
    </div>
  );

  const footer = isOwner ? (
    <div className="grid grid-cols-2 gap-2">
      <Button
        color="accent"
        className="flex items-center gap-2 h-11 mt-2 px-4 py-3.5 font-bold"
        onClick={onUpBalanceModal}
      >
        <Plus2Icon className="size-4.5" />
        {t("business.upBalance")}
      </Button>

      <Button
        variant="soft"
        color="gray"
        className="flex items-center gap-2 h-11 mt-2 px-4 py-3.5 font-bold"
        onClick={showModal}
      >
        <Case2Icon className="size-4.5" />
        {t("business.sell")}
      </Button>
    </div>
  ) : (
    <Button
      variant="soft"
      color="gray"
      className="flex items-center gap-2 h-11 mt-2 px-4 py-3.5 font-bold"
      onClick={showModal}
    >
      <Exit2Icon className="size-4.5" />
      {t("business.dismiss")}
    </Button>
  );

  return (
    <>
      {info?.hasFrozenImprovements && (
        <BusinessImprovementsPreviousContainer businessId={info?.id} device={device} />
      )}
      {isSellShow && (
        <BusinessSellContainer
          setIsShow={setIsSellShow}
          onSuccessSell={() => {
            onClose();
          }}
          device={device}
        />
      )}
      {isDismissShow && (
        <BusinessDismissModal
          title={info?.name}
          address={address}
          onClose={() => setIsDismissShow(false)}
          onConfirm={onDismissConfirm}
          isConfirming={isSelfRemovingLoading}
        />
      )}
      {isUpBalanceShow && (
        <BusinessUpBalanceContainer businessId={info?.id} onClose={closeUpBalanceModal} />
      )}
      <DeviceTabContentWrapper
        header={header}
        onClose={onClose}
        footer={footer}
        isStaticFirst={isFirstRender}
      >
        <div className="flex flex-col items-stretch gap-3.5 w-full">
          <BusinessView info={info} />
          <BusinessMainInfo info={info} />
        </div>
      </DeviceTabContentWrapper>
    </>
  );
}
